import { isAfter, isBefore, isMatch, isValid, parse, subMonths } from 'date-fns';
import parsePhoneNumber from 'libphonenumber-js';
import isEmpty from 'lodash/isEmpty';

type ValidationType =
	| 'email'
	| 'phone'
	| 'date'
	| 'card'
	| 'cardExpiry'
	| 'verificationCode'
	| 'typeahead'
	| 'checkbox'
	| 'default';

interface DateErrorMessages {
	past?: string;
	future?: string;
	formatted?: string;
}

interface DateOptions {
	past?: boolean;
	future?: boolean;
	format?: string;
}

interface PhoneData {
	dialCode?: string;
	number?: string;
}

interface TypeAheadData {
	displayValue?: string;
}
interface ErrorMessageOverrides {
	empty?: string;
	partial?: string | DateErrorMessages;
}
interface ValidationOptions {
	required?: boolean;
	errorMessages?: ErrorMessageOverrides;
	date?: DateOptions;
	cardExpiryDate?: string;
}

const getFormattedDateErrorMessage = (format: string): string =>
	`Please enter your date in ${format} format.`;

const validationMessages = {
	email: {
		empty: 'Email is required.',
		partial: 'Please enter a valid email address.',
	},
	phone: {
		empty: 'Phone Number is required.',
		partial: 'Please enter a valid phone number.',
	},
	card: {
		empty: 'Card Number is required.',
		partial: 'Please enter the last 4 digits of your credit card.',
	},
	verificationCode: {
		empty: 'Verification Code is required.',
		partial: 'Please enter a valid verification code.',
	},
	date: {
		empty: 'Date is required.',
		partial: {
			past: 'Please enter a past date.',
			future: 'Please enter a future date.',
		},
	},
};

const isEmailValid = (email: string): boolean => {
	const emailRegex =
		/^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;
	return emailRegex.test(email);
};

const isPhoneValid = (str = ''): boolean => {
	if (!/^[\d\s()+-]+$/.test(str)) {
		return false;
	} else {
		const phoneNumber = parsePhoneNumber(str.startsWith('+') ? str : `+${str}`);
		return (phoneNumber && phoneNumber.isValid()) || false;
	}
};

const buildDateFormatRegex = (originalFormat: string): RegExp => {
	const delimiters: string[] = originalFormat.match(/(\W+)/g) || [];
	const allMatches: string[] = originalFormat.match(/([a-zA-Z]+)|(\W+)?/g) || [];
	let regexString = '^';
	allMatches.forEach(match => {
		if (delimiters.includes(match)) {
			regexString += match;
		} else if (match.length > 0) {
			regexString += `\\w{${match.length}}`;
		}
	});
	regexString += '$';
	return new RegExp(regexString, 'g');
};

const isDateValid = (date?: Date | string, options?: ValidationOptions): boolean => {
	if (!date) {
		return false;
	}
	const dateOptions = options?.date;
	if (dateOptions?.format) {
		const regex = buildDateFormatRegex(dateOptions.format);
		if (typeof date === 'string' && (!regex.test(date) || !isMatch(date, dateOptions.format))) {
			//only want to return if not valid (no need to check past/future)
			return false;
		}
	}
	const dateObject = typeof date === 'string' ? new Date(date) : date;
	if (dateOptions?.past) {
		return isBefore(dateObject, new Date());
	} else if (dateOptions?.future) {
		return isAfter(dateObject, new Date());
	}
	return isValid(dateObject);
};

const isCodeValid = (code: string): boolean => {
	return /^[\d]{6}$/.test(code);
};

const isCardValid = (cardNumber: string): boolean => {
	return /^[\d]{4}$/.test(cardNumber);
};

const isCardExpiryValid = (date?: string, cardExpiryDate?: string): boolean => {
	if (!date) {
		return false;
	}
	const dateObject = parse(date, 'MM/yy', new Date());
	if (!cardExpiryDate) {
		const regex = buildDateFormatRegex('MM/yy');
		return regex.test(date) && isMatch(date, 'MM/yy');
	}
	const expiryDateObject = parse(cardExpiryDate, 'MM/yy', new Date());
	return isAfter(dateObject, subMonths(expiryDateObject, 1));
};

const validateField = (
	type: ValidationType,
	value?: string | Date | boolean,
	options?: ValidationOptions,
	data?: PhoneData | TypeAheadData
): [boolean, string, string] => {
	let errorMessages: ErrorMessageOverrides = {};
	let valid = false;
	let errorType: 'empty' | 'partial' = isEmpty(value) ? 'empty' : 'partial';
	const required = options?.required || false;

	switch (type) {
		case 'email':
			errorMessages = Object.assign(
				errorMessages,
				validationMessages['email'],
				options?.['errorMessages']
			) as ErrorMessageOverrides;
			valid = isEmailValid(value as string) || (!required && isEmpty(value));
			return [valid, valid ? '' : (errorMessages[errorType] as string), errorType];
		case 'phone':
			errorMessages = Object.assign(
				errorMessages,
				validationMessages['phone'],
				options?.['errorMessages']
			);
			if (required && (!(data as PhoneData)?.number || (data as PhoneData)?.number === '')) {
				return [false, errorMessages['empty'] as string, 'empty'];
			}
			valid = isPhoneValid(value as string) || (!required && isEmpty((data as PhoneData)?.number));
			return [valid, valid ? '' : (errorMessages[errorType] as string), errorType];
		case 'verificationCode':
			errorMessages = Object.assign(
				errorMessages,
				validationMessages['verificationCode'],
				options?.['errorMessages']
			);
			valid = isCodeValid(value as string) || (!required && isEmpty(value));
			return [valid, valid ? '' : (errorMessages[errorType] as string), errorType];
		case 'card':
			errorMessages = Object.assign(
				errorMessages,
				validationMessages['card'],
				options?.['errorMessages']
			);
			valid = isCardValid(value as string) || (!required && isEmpty(value));
			return [valid, valid ? '' : (errorMessages[errorType] as string), errorType];
		case 'date':
			errorMessages = Object.assign(
				errorMessages,
				validationMessages['date'],
				options?.['errorMessages']
			);
			//Format should be first, then past/future
			if (!required && isEmpty(value)) {
				return [true, '', ''];
			} else if (isEmpty(value) || typeof value === 'boolean') {
				return [false, errorMessages.empty as string, 'empty'];
			} else if (
				options?.date?.format &&
				!isDateValid(value, { date: { format: options.date.format } })
			) {
				return [
					false,
					(errorMessages['partial'] as DateErrorMessages)['formatted'] ||
						getFormattedDateErrorMessage(options.date.format || ''),
					'formatted',
				];
			} else if (options?.date?.past && !isDateValid(value, { date: { past: true } })) {
				return [false, (errorMessages['partial'] as DateErrorMessages)['past'] as string, 'past'];
			} else if (options?.date?.future && !isDateValid(value, { date: { future: true } })) {
				return [
					false,
					(errorMessages['partial'] as DateErrorMessages)['future'] as string,
					'future',
				];
			} else {
				return [true, '', ''];
			}
		case 'cardExpiry':
			errorMessages = Object.assign(
				errorMessages,
				{ empty: 'Card Expiration Date is required.' },
				{ partial: 'Credit card is expired.' },
				options?.['errorMessages']
			);

			if (!required && isEmpty(value)) {
				return [true, '', ''];
			} else if (isEmpty(value)) {
				return [false, errorMessages['empty'] as string, 'empty'];
			} else if (!isCardExpiryValid(value as string)) {
				return [
					false,
					(errorMessages['partial'] as DateErrorMessages)['formatted'] ||
						getFormattedDateErrorMessage(options?.date?.format || ''),
					'formatted',
				];
			}
			valid = isCardExpiryValid(value as string, options?.cardExpiryDate);
			return [
				valid,
				valid ? '' : ((errorMessages['partial'] as DateErrorMessages)['past'] as string),
				'past',
			];
		case 'typeahead':
			errorMessages = Object.assign(
				errorMessages,
				{ empty: 'Field is required.' },
				{ partial: 'Please select a valid option.' },
				options?.['errorMessages']
			);
			valid =
				!isEmpty(value) ||
				(!required && isEmpty(value) && isEmpty((data as TypeAheadData)?.displayValue));
			errorType =
				isEmpty(value) && isEmpty((data as TypeAheadData)?.displayValue) ? 'empty' : 'partial';
			return [valid, valid ? '' : (errorMessages[errorType] as string), errorType];
		case 'checkbox':
			errorMessages = Object.assign(
				errorMessages,
				{ empty: 'Checkbox is required.' },
				options?.['errorMessages']
			);
			valid = !!(required && value) || !required;
			return [valid, valid ? '' : (errorMessages.empty as string), 'empty'];
		default:
			errorMessages = Object.assign(
				errorMessages,
				{ empty: 'Field is required.' },
				options?.['errorMessages']
			);
			valid = !isEmpty(value) || (!required && isEmpty(value));
			return [valid, valid ? '' : (errorMessages.empty as string), 'empty'];
	}
};

export { isEmailValid, isPhoneValid, validateField };
export type { ValidationType, ValidationOptions };
